import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import PageLoader from './components/PageLoader';
import Home from './pages/Home';
import Landing from './landing/Landing';
import LandingNav from './landing/LandingNav';
import './landing/site3-theme.css';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ContactUs from './pages/ContactUs';
import Services from './pages/Services';
import GlobalCoverage from './pages/GlobalCoverage';
import Resources from './pages/Resources';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CountryDetail from './pages/CountryDetail';
import WhyTrueLog from './pages/WhyTrueLog';
import CaseStudies from './pages/CaseStudies';
import NotFound from './pages/NotFound';
import Seo from './seo/Seo';

// Service pages (By Services)
import IORSolutions from './pages/services/IORSolutions';
import FreightForwarding from './pages/services/FreightForwarding';
import GlobalFulfillment from './pages/services/GlobalFulfillment';
import Compliance from './pages/services/Compliance';

// Client Type pages (By Client Type)
import SaasDigitalPlatforms from './pages/solutions/SaasDigitalPlatforms';
import HyperscalersCloud from './pages/solutions/HyperscalersCloud';
import GlobalEnterprises from './pages/solutions/GlobalEnterprises';
import OemsHardware from './pages/solutions/OemsHardware';
import SystemIntegratorsMsps from './pages/solutions/SystemIntegratorsMsps';
import TechStartups from './pages/solutions/TechStartups';

// Industry pages (By Industry)
import TechnologySoftware from './pages/industries/TechnologySoftware';
import FinancialServices from './pages/industries/FinancialServices';
import MediaEntertainment from './pages/industries/MediaEntertainment';
import IndustrialManufacturing from './pages/industries/IndustrialManufacturing';
import EducationResearch from './pages/industries/EducationResearch';
import GovernmentPublicSector from './pages/industries/GovernmentPublicSector';

// The /site1, /site2 and /site3 preview builds swap the homepage for the cosmos landing page.
const LANDING_MODE = ['cosmos', 'hardware', 'warehouse'].includes(process.env.REACT_APP_LANDING || '');
const SITE3_MODE = process.env.REACT_APP_LANDING === 'warehouse';

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const pageTransition = {
  type: 'tween' as const,
  ease: 'easeInOut' as const,
  duration: 0.3,
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.main
        className={SITE3_MODE && location.pathname !== '/' ? 'site3-subpage' : undefined}
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={pageTransition}
      >
        <Routes location={location}>
          <Route path="/" element={LANDING_MODE ? <Landing /> : <Home />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/services" element={<Services />} />
          <Route path="/global-coverage" element={<GlobalCoverage />} />
          <Route path="/why-truelog" element={<WhyTrueLog />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* Terms of Use was removed (Oct 2026 feedback); old links land on the legal page */}
          <Route path="/terms-of-use" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/countries/:slug" element={<CountryDetail />} />

          {/* By Services */}
          <Route path="/services/cross-border-compliance" element={<IORSolutions />} />
          <Route path="/services/global-transport-freight" element={<FreightForwarding />} />
          <Route path="/services/warehousing-fulfilment" element={<GlobalFulfillment />} />

          {/* Value Added Services pages (ICTLogistics, DevicePreparation,
              DeploymentFieldServices) are on hold until next year, so their
              URLs fall back to the services overview. */}
          <Route path="/services/device-preparation" element={<Navigate to="/services" replace />} />
          <Route path="/services/deployment-field-services" element={<Navigate to="/services" replace />} />
          <Route path="/services/it-asset-lifecycle" element={<Navigate to="/services" replace />} />

          {/* Legacy service routes (keep for backwards compatibility) */}
          <Route path="/services/freight-forwarding" element={<FreightForwarding />} />
          <Route path="/services/global-fulfillment" element={<GlobalFulfillment />} />
          <Route path="/services/ict-logistics" element={<Navigate to="/services" replace />} />
          <Route path="/services/ior-eor-solutions" element={<IORSolutions />} />
          <Route path="/services/compliance" element={<Compliance />} />

          {/* By Client Type */}
          <Route path="/solutions/saas-digital-platforms" element={<SaasDigitalPlatforms />} />
          <Route path="/solutions/hyperscalers-cloud" element={<HyperscalersCloud />} />
          <Route path="/solutions/global-enterprises" element={<GlobalEnterprises />} />
          <Route path="/solutions/oems-hardware" element={<OemsHardware />} />
          <Route path="/solutions/system-integrators-msps" element={<SystemIntegratorsMsps />} />
          <Route path="/solutions/tech-startups" element={<TechStartups />} />

          {/* By Industry */}
          <Route path="/industries/technology-software" element={<TechnologySoftware />} />
          <Route path="/industries/financial-services" element={<FinancialServices />} />
          <Route path="/industries/media-entertainment" element={<MediaEntertainment />} />
          <Route path="/industries/industrial-manufacturing" element={<IndustrialManufacturing />} />
          <Route path="/industries/education-research" element={<EducationResearch />} />
          <Route path="/industries/government-public-sector" element={<GovernmentPublicSector />} />

          {/* Anything else: real 404 page (nginx serves its prerendered copy with status 404) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
};

// Site chrome. The cosmos landing brings its own overlay nav, so the standard
// header and progress bar are skipped on "/" in landing mode (loader too).
const Shell: React.FC = () => {
  const location = useLocation();
  const landing = LANDING_MODE && location.pathname === '/';

  React.useEffect(() => {
    if (SITE3_MODE) window.scrollTo(0, 0);
  }, [location.pathname]);

  // Prerendered pages (scripts/prerender.mjs) keep #root hidden until React has
  // mounted so the static snapshot never flashes before the animated tree.
  React.useEffect(() => {
    document.documentElement.removeAttribute('data-prerendered');
  }, []);

  return (
    <div className={`App relative min-h-screen overflow-x-clip bg-transparent text-secondary-900 transition-colors duration-300 dark:text-gray-100${SITE3_MODE ? ' site3-theme' : ''}`}>
      <Seo />
      {!LANDING_MODE && <PageLoader />}
      {!landing && !SITE3_MODE && <ScrollProgress />}
      {SITE3_MODE ? <LandingNav /> : !landing && <Header />}
      <AnimatedRoutes />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router basename={process.env.PUBLIC_URL}>
        <Shell />
      </Router>
    </ThemeProvider>
  );
}

export default App;
